import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import PageHeader from './ui/PageHeader';
import {
  FileText, Upload, Download, Trash2, Edit, Search, Filter, Grid, List,
  FolderOpen, Clock, Eye, Users, CheckCircle2, AlertTriangle, RefreshCw,
  Plus, X, ChevronRight, File, FileSpreadsheet, FileImage, MoreVertical,
  Shield, Lock, Globe, Building, Tag, Calendar, History, UserCheck,
  Loader2, ArrowUpDown, ExternalLink, FileCheck, FolderPlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons mapping
const fileTypeIcons = {
  'application/pdf': FileText,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileText,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileSpreadsheet,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': FileImage,
  'image/png': FileImage,
  'image/jpeg': FileImage,
  'text/plain': FileText,
};

// File type icons mapping - use direct lookup from fileTypeIcons

// Access level colors
const accessLevelColors = {
  public: 'bg-green-100 text-green-700',
  internal: 'bg-blue-100 text-blue-700',
  restricted: 'bg-yellow-100 text-yellow-700',
  confidential: 'bg-red-100 text-red-700',
};

const accessLevelIcons = {
  public: Globe,
  internal: Building,
  restricted: Shield,
  confidential: Lock,
};

// Category colors
const categoryColors = {
  policies: '#3b82f6',
  procedures: '#10b981',
  training: '#f59e0b',
  forms: '#8b5cf6',
  reports: '#ef4444',
  permits: '#06b6d4',
  msds: '#ec4899',
  emergency: '#dc2626',
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Main Document Management Component
const DocumentManagement = () => {
  const [activeTab, setActiveTab] = useState('library');
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('all');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const token = localStorage.getItem('auth_token');

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      const [docsRes, catsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/documents`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/documents/categories`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/documents/stats`, { headers: authHeaders })
      ]);

      setDocuments(docsRes.data.documents || []);
      setCategories(catsRes.data.categories || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesAccess = selectedAccessLevel === 'all' || doc.access_level === selectedAccessLevel;
    return matchesSearch && matchesCategory && matchesAccess;
  }).sort((a, b) => {
    const aVal = a[sortBy] || '';
    const bVal = b[sortBy] || '';
    if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  // Delete document
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to archive this document?')) return;
    
    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/documents/${docId}`, { headers: authHeaders });
      fetchData();
    } catch (err) {
      setError('Failed to delete document');
    }
  };

  // View document details
  const handleViewDocument = async (doc) => {
    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}`, { headers: authHeaders });
      setSelectedDocument(response.data);
    } catch (err) {
      setError('Failed to load document details');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading document library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <PageHeader
        title="Document Management"
        subtitle="Upload, organize & manage safety documents"
        icon={FolderOpen}
        accentColor="blue"
        actions={
          <div className="flex gap-2">
            <Button onClick={() => setShowUploadModal(true)} className="bg-white/20 hover:bg-white/30 text-white border-white/30">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
            <Button onClick={fetchData} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        }
        stats={[
          { icon: FileText, label: 'Documents', value: stats?.total_documents || 0, color: 'blue' },
          { icon: FolderOpen, label: 'Categories', value: categories.length, color: 'purple' },
          { icon: AlertTriangle, label: 'Expiring', value: stats?.expiring_soon || 0, color: 'yellow' },
          { icon: UserCheck, label: 'Pending Ack', value: stats?.pending_acknowledgment || 0, color: 'orange' }
        ]}
      />

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>
          </Alert>
        )}

        {/* Document Detail View */}
        {selectedDocument && (
          <DocumentDetailView
            document={selectedDocument}
            onClose={() => setSelectedDocument(null)}
            onNewVersion={() => {
              setShowVersionModal(true);
            }}
            onRefresh={fetchData}
            token={token}
          />
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <UploadModal
            categories={categories}
            onClose={() => setShowUploadModal(false)}
            onSuccess={() => {
              setShowUploadModal(false);
              fetchData();
            }}
            token={token}
          />
        )}

        {/* Main Content */}
        {!selectedDocument && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 rounded-xl bg-gray-100 p-1">
              <TabsTrigger value="library" className="rounded-lg">
                <FileText className="w-4 h-4 mr-2 hidden sm:inline" />
                Library
              </TabsTrigger>
              <TabsTrigger value="categories" className="rounded-lg">
                <FolderOpen className="w-4 h-4 mr-2 hidden sm:inline" />
                Categories
              </TabsTrigger>
              <TabsTrigger value="dashboard" className="rounded-lg">
                <Grid className="w-4 h-4 mr-2 hidden sm:inline" />
                Dashboard
              </TabsTrigger>
            </TabsList>

            <TabsContent value="library">
              <DocumentLibrary
                documents={filteredDocuments}
                categories={categories}
                viewMode={viewMode}
                setViewMode={setViewMode}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedAccessLevel={selectedAccessLevel}
                setSelectedAccessLevel={setSelectedAccessLevel}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                onView={handleViewDocument}
                onDelete={handleDelete}
              />
            </TabsContent>

            <TabsContent value="categories">
              <CategoryManager
                categories={categories}
                onRefresh={fetchData}
                token={token}
              />
            </TabsContent>

            <TabsContent value="dashboard">
              <DocumentDashboard stats={stats} categories={categories} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

// Document Library Component
const DocumentLibrary = ({
  documents, categories, viewMode, setViewMode, searchTerm, setSearchTerm,
  selectedCategory, setSelectedCategory, selectedAccessLevel, setSelectedAccessLevel,
  sortBy, setSortBy, sortOrder, setSortOrder, onView, onDelete
}) => {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
              ))}
            </select>

            {/* Access Level Filter */}
            <select
              value={selectedAccessLevel}
              onChange={(e) => setSelectedAccessLevel(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Access Levels</option>
              <option value="public">Public</option>
              <option value="internal">Internal</option>
              <option value="restricted">Restricted</option>
              <option value="confidential">Confidential</option>
            </select>

            {/* Sort */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="created_at-desc">Newest First</option>
              <option value="created_at-asc">Oldest First</option>
              <option value="title-asc">Name A-Z</option>
              <option value="title-desc">Name Z-A</option>
              <option value="view_count-desc">Most Viewed</option>
              <option value="download_count-desc">Most Downloaded</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{documents.length} documents found</p>
      </div>

      {/* Document Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map(doc => (
            <DocumentCard key={doc.id} document={doc} onView={onView} onDelete={onDelete} />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => (
            <DocumentListItem key={doc.id} document={doc} onView={onView} onDelete={onDelete} />
          ))}
        </div>
      )}

      {documents.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold">No Documents Found</h3>
            <p className="text-slate-600">Try adjusting your search or filters.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Document Card Component
const DocumentCard = ({ document, onView, onDelete }) => {
  const fileType = document.file_type;
  const accessLevel = document.access_level;
  const FileIconComponent = fileTypeIcons[fileType] || File;
  const AccessIconComponent = accessLevelIcons[accessLevel] || Shield;

  return (
    <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => onView(document)}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: categoryColors[document.category] + '20' }}
          >
            <FileIconComponent className="w-6 h-6" style={{ color: categoryColors[document.category] }} />
          </div>
          <div className="flex items-center gap-2">
            <Badge className={accessLevelColors[accessLevel]}>
              <AccessIconComponent className="w-3 h-3 mr-1" />
              {document.access_level}
            </Badge>
          </div>
        </div>

        <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {document.title}
        </h3>
        <p className="text-sm text-slate-600 line-clamp-2 mb-4">{document.description}</p>

        <div className="flex flex-wrap gap-1 mb-4">
          {document.tags?.slice(0, 3).map((tag, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              <Tag className="w-3 h-3 mr-1" />{tag}
            </Badge>
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-slate-500 pt-4 border-t">
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" /> {document.view_count}
          </span>
          <span className="flex items-center gap-1">
            <Download className="w-3 h-3" /> {document.download_count}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> v{document.version}
          </span>
          <span>{formatFileSize(document.file_size)}</span>
        </div>

        {document.requires_acknowledgment && (
          <div className="mt-3 p-2 bg-orange-50 rounded text-xs text-orange-700 flex items-center gap-1">
            <UserCheck className="w-3 h-3" /> Requires acknowledgment
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Document List Item Component
const DocumentListItem = ({ document, onView, onDelete }) => {
  const fileType = document.file_type;
  const accessLevel = document.access_level;
  const FileIconComponent = fileTypeIcons[fileType] || File;

  return (
    <Card className="hover:shadow-md transition-all cursor-pointer" onClick={() => onView(document)}>
      <CardContent className="py-4">
        <div className="flex items-center gap-4">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: categoryColors[document.category] + '20' }}
          >
            <FileIconComponent className="w-5 h-5" style={{ color: categoryColors[document.category] }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium truncate">{document.title}</h3>
              <Badge className={`${accessLevelColors[accessLevel]} text-xs`}>
                {accessLevel}
              </Badge>
            </div>
            <p className="text-sm text-slate-500 truncate">{document.file_name}</p>
          </div>

          <div className="hidden md:flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" /> {document.view_count}
            </span>
            <span className="flex items-center gap-1">
              <Download className="w-4 h-4" /> {document.download_count}
            </span>
            <span>v{document.version}</span>
            <span>{formatFileSize(document.file_size)}</span>
          </div>

          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(document.id)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Document Detail View Component
const DocumentDetailView = ({ document, onClose, onNewVersion, onRefresh, token }) => {
  const fileType = document.file_type;
  const accessLevel = document.access_level;
  const FileIconComponent = fileTypeIcons[fileType] || File;
  const AccessIconComponent = accessLevelIcons[accessLevel] || Shield;
  const [acknowledging, setAcknowledging] = useState(false);

  const handleAcknowledge = async () => {
    try {
      setAcknowledging(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API_URL}/api/documents/${document.id}/acknowledge`, {
        user_id: user.id || 'anonymous',
        user_name: user.username || user.email || 'Anonymous User'
      }, { headers: authHeaders });
      
      alert('Document acknowledged successfully');
      onRefresh();
    } catch (err) {
      alert('Failed to acknowledge document');
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <Button variant="ghost" onClick={onClose}>
                <X className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div
                className="w-16 h-16 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: categoryColors[document.category] + '20' }}
              >
                <FileIconComponent className="w-8 h-8" style={{ color: categoryColors[document.category] }} />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-2">{document.title}</h1>
                <div className="flex items-center gap-3">
                  <Badge className={accessLevelColors[accessLevel]}>
                    <AccessIconComponent className="w-3 h-3 mr-1" />
                    {accessLevel}
                  </Badge>
                  <Badge variant="outline">{document.category}</Badge>
                  <Badge variant="outline">v{document.version}</Badge>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {document.has_file && (
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
              <Button variant="outline" onClick={onNewVersion}>
                <Upload className="w-4 h-4 mr-2" />
                New Version
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">{document.description || 'No description available.'}</p>
              
              {document.tags?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {document.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline">
                      <Tag className="w-3 h-3 mr-1" />{tag}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Version History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Version History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {document.versions?.length > 0 ? (
                <div className="space-y-4">
                  {document.versions.map((version, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                        {version.version}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{version.uploaded_by}</span>
                          <span className="text-sm text-slate-500">
                            {new Date(version.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{version.changes}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No version history available.</p>
              )}
            </CardContent>
          </Card>

          {/* Acknowledgments */}
          {document.requires_acknowledgment && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Acknowledgments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleAcknowledge}
                  disabled={acknowledging}
                  className="mb-4 bg-green-600 hover:bg-green-700"
                >
                  {acknowledging ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Acknowledge Document
                </Button>

                {document.acknowledgments?.length > 0 ? (
                  <div className="space-y-2">
                    {document.acknowledgments.map((ack, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <span className="font-medium">{ack.user_name}</span>
                        <span className="text-sm text-slate-500">
                          {new Date(ack.acknowledged_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500">No acknowledgments yet.</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Document Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-slate-500">File Name</span>
                <span className="font-medium truncate max-w-32">{document.file_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">File Size</span>
                <span className="font-medium">{formatFileSize(document.file_size)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Department</span>
                <span className="font-medium capitalize">{document.department || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Uploaded By</span>
                <span className="font-medium">{document.uploaded_by}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Created</span>
                <span className="font-medium">{new Date(document.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Updated</span>
                <span className="font-medium">{new Date(document.updated_at).toLocaleDateString()}</span>
              </div>
              {document.expiry_date && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Expires</span>
                  <span className="font-medium text-orange-600">{new Date(document.expiry_date).toLocaleDateString()}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Statistics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="flex items-center gap-2 text-blue-700">
                  <Eye className="w-4 h-4" /> Views
                </span>
                <span className="text-2xl font-bold text-blue-600">{document.view_count}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="flex items-center gap-2 text-green-700">
                  <Download className="w-4 h-4" /> Downloads
                </span>
                <span className="text-2xl font-bold text-green-600">{document.download_count}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ categories, onClose, onSuccess, token }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'policies',
    tags: '',
    department: '',
    access_level: 'internal',
    expiry_date: '',
    requires_acknowledgment: false
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.category) {
      alert('Please fill in required fields');
      return;
    }

    try {
      setUploading(true);
      const authHeaders = { Authorization: `Bearer ${token}` };
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('category', formData.category);
      data.append('tags', formData.tags);
      data.append('department', formData.department);
      data.append('access_level', formData.access_level);
      data.append('expiry_date', formData.expiry_date);
      data.append('requires_acknowledgment', formData.requires_acknowledgment);
      data.append('uploaded_by', user.username || user.email || 'admin');
      if (file) {
        data.append('file', file);
      }

      await axios.post(`${API_URL}/api/documents`, data, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data'
        }
      });

      onSuccess();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (!formData.title) {
        setFormData(prev => ({ ...prev, title: droppedFile.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Document
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-10 h-10 text-blue-500" />
                  <div className="text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-slate-500">{formatFileSize(file.size)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFile(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p className="text-slate-600 mb-2">Drag and drop your file here, or</p>
                  <label className="cursor-pointer">
                    <span className="text-blue-600 hover:underline">browse files</span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0];
                        if (selectedFile) {
                          setFile(selectedFile);
                          if (!formData.title) {
                            setFormData(prev => ({ ...prev, title: selectedFile.name.replace(/\.[^/.]+$/, '') }));
                          }
                        }
                      }}
                    />
                  </label>
                </>
              )}
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Access Level</label>
                <select
                  value={formData.access_level}
                  onChange={(e) => setFormData(prev => ({ ...prev, access_level: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="public">Public</option>
                  <option value="internal">Internal</option>
                  <option value="restricted">Restricted</option>
                  <option value="confidential">Confidential</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Department</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., safety, operations"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Comma-separated tags"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requires_ack"
                  checked={formData.requires_acknowledgment}
                  onChange={(e) => setFormData(prev => ({ ...prev, requires_acknowledgment: e.target.checked }))}
                  className="w-4 h-4"
                />
                <label htmlFor="requires_ack" className="text-sm">Requires acknowledgment</label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading} className="bg-blue-600 hover:bg-blue-700">
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Category Manager Component
const CategoryManager = ({ categories, onRefresh, token }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#3b82f6' });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!newCategory.name) return;

    try {
      setCreating(true);
      const authHeaders = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/documents/categories`, newCategory, { headers: authHeaders });
      setShowCreateModal(false);
      setNewCategory({ name: '', description: '', color: '#3b82f6' });
      onRefresh();
    } catch (err) {
      alert('Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/documents/categories/${categoryId}`, { headers: authHeaders });
      onRefresh();
    } catch (err) {
      alert('Failed to delete category');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Document Categories</h2>
        <Button onClick={() => setShowCreateModal(true)}>
          <FolderPlus className="w-4 h-4 mr-2" />
          New Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {categories.map(category => (
          <Card key={category.id || category.name} className="hover:shadow-md transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: category.color + '20' }}
                >
                  <FolderOpen className="w-5 h-5" style={{ color: category.color }} />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(category.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <h3 className="font-semibold capitalize mb-1">{category.name}</h3>
              <p className="text-sm text-slate-600 mb-2">{category.description}</p>
              <Badge variant="outline">{category.document_count || 0} documents</Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Category Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Create Category</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <input
                  type="color"
                  value={newCategory.color}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-10 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Document Dashboard Component
const DocumentDashboard = ({ stats, categories }) => {
  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-t-4 border-t-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-600">{stats.total_documents}</p>
                <p className="text-sm text-slate-600">Total Documents</p>
              </div>
              <FileText className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-purple-600">{categories.length}</p>
                <p className="text-sm text-slate-600">Categories</p>
              </div>
              <FolderOpen className="w-10 h-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-yellow-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-yellow-600">{stats.expiring_soon}</p>
                <p className="text-sm text-slate-600">Expiring Soon</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-orange-600">{stats.pending_acknowledgment}</p>
                <p className="text-sm text-slate-600">Pending Acks</p>
              </div>
              <UserCheck className="w-10 h-10 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* By Category & By Access Level */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Documents by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.by_category || {}).map(([category, count]) => (
                <div key={category} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: categoryColors[category] || '#64748b' }}
                    />
                    <span className="capitalize">{category}</span>
                  </div>
                  <Badge>{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Documents by Access Level</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.by_access_level || {}).map(([level, count]) => {
                const Icon = accessLevelIcons[level] || Shield;
                return (
                  <div key={level} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: accessLevelColors[level]?.split(' ')[0] + '20' }}>
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4" />
                      <span className="capitalize">{level}</span>
                    </div>
                    <Badge className={accessLevelColors[level]}>{count}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Document Status */}
      <Card>
        <CardHeader>
          <CardTitle>Documents by Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-green-600">{stats.by_status?.active || 0}</p>
              <p className="text-sm text-green-700">Active</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-yellow-600">{stats.by_status?.draft || 0}</p>
              <p className="text-sm text-yellow-700">Draft</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-slate-600">{stats.by_status?.archived || 0}</p>
              <p className="text-sm text-slate-700">Archived</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Uploads */}
      {stats.recent_uploads?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Uploads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_uploads.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-500" />
                    <span className="font-medium">{doc.title}</span>
                  </div>
                  <span className="text-sm text-slate-500">
                    {new Date(doc.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DocumentManagement;
